import sendData from 'redux/actions/sendData';
import { HASH_REDEEM_STARTED, HASH_REDEEM_SUCCESS, HASH_REDEEM_FAILED, HASH_AUTH_CLEAR } from 'redux/actions/types';
import { AUTHENTICATE_ENDPOINT } from './shareLinkConfig';

// Set to true while a backend isn't reachable — derives a fake token from the hash.
const USE_STUBBED_RESPONSE = false;

// Probe path on the normal /api/v1 namespace for a given scope. Used to detect
// whether the currently-logged-in user can already see this resource normally,
// in which case we bypass hash authentication entirely.
const probeUrlFor = (scope) => {
  if (!scope) return null;
  switch (scope.kind) {
    case 'assessment':
      return `assessments/${scope.id}`;
    case 'task':
      return `tasks/${scope.id}`;
    case 'message':
      return `messages/${scope.id}`;
    case 'document':
      // A Library document lives under a realm-scoped Edition. Probe the Editions
      // show route (the Editions JSON:API adapter applies RealmScope), so a
      // logged-in user WITHOUT access gets a 404 and we fall through to anonymous
      // hash auth instead of wrongly reporting normal access. The unscoped
      // edition-documents collection returns 200 even for users without access,
      // so it must NOT be used to detect normal access.
      if (!scope.courseId && scope.editionId) {
        return `editions/${scope.editionId}`;
      }
      return scope.courseId ? `edition-documents/${scope.id}` : `editions/${scope.id}`;
    default:
      return null;
  }
};

const stubRedeem = (hash, scope) => {
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
  return {
    hashToken: `stub.${hash}`,
    scope,
    expiresAt,
    hasNormalAccess: false,
  };
};

// If the user already has a normal session, see if they can read this resource
// without the hash. Returns true on 2xx, false on any error. We never rethrow:
// a probe failure (network blip, 5xx, anything else) must not stop the
// redemption flow — we just fall through to anonymous hash auth so the user
// reliably lands inside the shared content instead of in a reload loop.
const probeNormalAccess = async (scope) => {
  const url = probeUrlFor(scope);
  if (!url) return false;
  try {
    await sendData({ url, method: 'GET' });
    return true;
  } catch (err) {
    return false;
  }
};

export const redeemHash = (hash, scope) => async (dispatch, getState) => {
  dispatch({ type: HASH_REDEEM_STARTED });

  try {
    const hasUserSession = !!getState()?.auth?.accessToken;

    if (hasUserSession) {
      const ok = await probeNormalAccess(scope);
      if (ok) {
        const payload = { hash, hashToken: null, scope, expiresAt: null, hasNormalAccess: true };
        dispatch({ type: HASH_REDEEM_SUCCESS, payload });
        return payload;
      }
    }

    let payload;
    if (USE_STUBBED_RESPONSE) {
      payload = { hash, ...stubRedeem(hash, scope) };
    } else {
      const resp = await sendData({ url: AUTHENTICATE_ENDPOINT, method: 'POST', data: { hash } }, { useAuth: false, useNamespace: false, useContentType: 'application/json' });
      const d = resp?.data || {};
      const accessToken = d.access_token || d.accessToken;
      const expiresAt = d.expires_at || d.expiresAt;
      if (!accessToken) throw new Error('Link could not be redeemed');
      payload = { hash, hashToken: accessToken, scope, expiresAt, hasNormalAccess: false };
    }

    dispatch({ type: HASH_REDEEM_SUCCESS, payload });
    return payload;
  } catch (err) {
    dispatch({ type: HASH_REDEEM_FAILED, payload: err?.message || 'Link could not be redeemed' });
    throw err;
  }
};

export const clearHashAuth = () => ({ type: HASH_AUTH_CLEAR });
